import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import './AdminPage.css';

function AdminPage() {
  const [reservations, setReservations] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayReservations, setSelectedDayReservations] = useState([]); // toutes les résas du jour cliqué
  const [selectedReservation, setSelectedReservation] = useState(null); // celle affichée en détail
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('authToken');

  const loadReservations = () => {
    fetch(`${API_URL}/reservations`, {
      headers: { 'Authorization': 'Bearer ' + token },
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        setReservations(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Erreur fetch réservations:", err);
        setError("Impossible de charger les réservations");
        setLoading(false);
      });
  };

  // Check authentication on mount
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Récupère les réservations pour un jour donné (FIX: évite toISOString() timezone issue)
  const getReservationsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD en local
    return reservations.filter(r => r.dateJour === dateStr);
  };

  // Marque une réservation comme consultée (fait disparaître le badge "nouveau")
  const markAsVue = (reservation) => {
    if (!reservation || reservation.vue) return;
    fetch(`${API_URL}/reservations/${reservation.id}/vue`, {
      method: 'PUT',
      headers: { 'Authorization': 'Bearer ' + token }
    })
      .then(res => res.ok ? res.json() : null)
      .then(updated => {
        if (updated) {
          setReservations(prev => prev.map(r => r.id === updated.id ? updated : r));
        }
      })
      .catch(() => {});
  };

  const openReservation = (reservation) => {
    setSelectedReservation(reservation);
    markAsVue(reservation);
  };

  const cancelReservation = (reservation) => {
    if (!window.confirm(`Annuler la réservation de ${reservation.name} le ${new Date(reservation.dateJour).toLocaleDateString('fr-FR')} à ${new Date(reservation.dateDebut).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})} ?`)) {
      return;
    }
    fetch(`${API_URL}/reservations/${reservation.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + token }
    })
      .then(res => {
        if (!res.ok) throw new Error();
        setReservations(prev => prev.filter(r => r.id !== reservation.id));
        setSelectedReservation(null);
      })
      .catch(() => setError("Erreur lors de l'annulation de la réservation"));
  };

  // Génère les jours du mois
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // FIX: Convertir dimanche=0 en dimanche=6 pour que lundi soit premier
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1; // dimanche=6, lundi=0, ..., samedi=5
  };

  // Crée le calendrier
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    const days = [];

    // Jours vides avant le 1er du mois
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayReservations = getReservationsForDate(date);
      const hasNouvelle = dayReservations.some(r => !r.vue);
      const isToday =
        date.toDateString() === new Date().toDateString();

      days.push(
        <div
          key={`day-${day}`}
          className={`calendar-day ${dayReservations.length > 0 ? 'booked' : ''} ${isToday ? 'today' : ''} ${hasNouvelle ? 'has-new' : ''}`}
          onClick={() => {
            if (dayReservations.length === 1) {
              openReservation(dayReservations[0]);
            } else if (dayReservations.length > 1) {
              setSelectedDayReservations(dayReservations);
            }
          }}
        >
          <span className="day-number">{day}</span>
          {dayReservations.length > 0 && (
            <span className="booked-indicator">
              {dayReservations.length > 1 ? dayReservations.length : '●'}
            </span>
          )}
          {hasNouvelle && <span className="new-dot" title="Nouvelle réservation"></span>}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    navigate('/login');
  };

  if (loading) return <div className="admin-page"><p>Chargement...</p></div>;

  const nouvelles = reservations.filter(r => !r.vue).length;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>
          Calendrier des réservations
          {nouvelles > 0 && (
            <span className="new-badge">{nouvelles} nouvelle{nouvelles > 1 ? 's' : ''}</span>
          )}
        </h1>
        <div className="admin-header-actions">
          <button onClick={() => navigate('/admin/vitrine')} className="gestion-button">Vitrine (Collections)</button>
          <button onClick={() => navigate('/admin/disponibilites')} className="gestion-button">Disponibilités</button>
          <button onClick={() => navigate('/admin/gestion')} className="gestion-button">Gérer prestations & capsules</button>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="calendar-container">
        <div className="calendar-header">
          <button onClick={previousMonth} className="nav-button">&lt; Précédent</button>
          <h2>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button onClick={nextMonth} className="nav-button">Suivant &gt;</button>
        </div>

        <div className="calendar-grid">
          <div className="weekday-header">Lun</div>
          <div className="weekday-header">Mar</div>
          <div className="weekday-header">Mer</div>
          <div className="weekday-header">Jeu</div>
          <div className="weekday-header">Ven</div>
          <div className="weekday-header">Sam</div>
          <div className="weekday-header">Dim</div>

          {renderCalendar()}
        </div>
      </div>

      {selectedDayReservations.length > 0 && (
        <div className="modal-overlay" onClick={() => setSelectedDayReservations([])}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedDayReservations([])}>✕</button>
            <h2>Réservations du jour</h2>
            <div className="day-reservations-list">
              {selectedDayReservations
                .slice()
                .sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut))
                .map(r => (
                  <div
                    key={r.id}
                    className="day-reservation-item"
                    onClick={() => { openReservation(r); setSelectedDayReservations([]); }}
                  >
                    <strong>{new Date(r.dateDebut).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</strong>
                    {' — '}{r.name} ({r.prestation.titre})
                    {!r.vue && <span className="inline-new-badge">nouveau</span>}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {selectedReservation && (
        <div className="modal-overlay" onClick={() => setSelectedReservation(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedReservation(null)}>✕</button>
            <h2>Détails de la réservation</h2>

            <div className="reservation-details">
              <div className="detail-row">
                <span className="detail-label">Nom:</span>
                <span className="detail-value">{selectedReservation.name}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Téléphone:</span>
                <span className="detail-value">{selectedReservation.phone}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Date:</span>
                <span className="detail-value">
                  {new Date(selectedReservation.dateJour).toLocaleDateString('fr-FR')}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Horaire:</span>
                <span className="detail-value">
                  {new Date(selectedReservation.dateDebut).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})} 
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Prestation:</span>
                <span className="detail-value">{selectedReservation.prestation.titre}</span>
              </div>

            {selectedReservation.capsule && (
                <div className="detail-row">
                  <span className="detail-label">Type de capsule:</span>
                  <span className="detail-value">{selectedReservation.capsule.titre}</span>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Dépose:</span>
                <span className="detail-value">{selectedReservation.depose ? 'Oui' : 'Non'}</span>
              </div>


              <div className="detail-row">
                <span className="detail-label">Statut:</span>
                <span className="detail-value">{selectedReservation.status}</span>
              </div>

              {selectedReservation.instagram && (
                <div className="detail-row">
                  <span className="detail-label">Instagram:</span>
                  <span className="detail-value">{selectedReservation.instagram}</span>
                </div>
              )}

              {selectedReservation.description && (
                <div className="detail-row">
                  <span className="detail-label">Description:</span>
                  <span className="detail-value description-text">
                    {selectedReservation.description}
                  </span>
                </div>
              )}

              {selectedReservation.photoFilenames && selectedReservation.photoFilenames.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Photos:</span>
                  <div className="photos-grid">
                    {selectedReservation.photoFilenames.map((filename, idx) => (
                      <img
                        key={idx}
                        src={`${API_URL}/uploads/${filename}`}
                        alt={`Photo ${idx + 1}`}
                        className="photo-thumbnail"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button className="cancel-reservation-button" onClick={() => cancelReservation(selectedReservation)}>
              Annuler cette réservation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
